import { useEffect, useEffectEvent, useState } from 'react';
import { useAuthStore } from '@/stores';
import { useStanRouter } from '@/hooks/useStanRouter';
import { MFAModal } from '@/features/auth/components/MFAModal';
import { NO_AUTH_ROUTES } from '@/lib/constants';

interface AuthProviderProps {
    children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [mfaModalOpen, setMfaModalOpen] = useState(false);
    const [authRedirect, setAuthRedirect] = useState<string | undefined>(undefined);
    const initialize = useAuthStore(state => state.initialize);
    const isAuthenticated = useAuthStore(state => state.isAuthenticated);
    const storeInitialized = useAuthStore(state => state.storeInitialized);
    const mfaToken = useAuthStore(state => state.mfaToken);
    const mfaConfigName = useAuthStore(state => state.mfaConfigName);
    const mfaLoginEndpoint = useAuthStore(state => state.mfaLoginEndpoint);
    const { push } = useStanRouter();

    const handleInitialize = useEffectEvent(() => {
        initialize();
    });

    const handleRedirectToLogin = useEffectEvent((path: string) => {
        setAuthRedirect(path);
        push('/login');
    });

    const handleRedirectAfterLogin = useEffectEvent((redirect: string) => {
        setAuthRedirect(undefined);
        push(redirect);
    });

    useEffect(() => {
        handleInitialize();
    }, []);

    useEffect(() => {
        const currentPath = window.location.pathname;
        // Filter out the routes that are allowed while unauthorized
        if (storeInitialized && !isAuthenticated && !NO_AUTH_ROUTES.includes(currentPath.toLowerCase())) {
            // Redirect to login and remember the page they were trying to access
            handleRedirectToLogin(currentPath);
        }
    }, [isAuthenticated, storeInitialized]);

    useEffect(() => {
        // Send people back to the page they were trying to access after login
        if (isAuthenticated && authRedirect) {
            handleRedirectAfterLogin(authRedirect);
        }
    }, [isAuthenticated, authRedirect]);

    useEffect(() => {
        if (mfaToken) {
            setMfaModalOpen(true);
        } else {
            setMfaModalOpen(false);
        }
    }, [mfaToken]);

    return (
        <>
            {mfaToken && mfaLoginEndpoint && (
                <MFAModal
                    isOpen={mfaModalOpen}
                    configName={mfaConfigName}
                    onClose={() => setMfaModalOpen(false)}
                    loginEndpoint={mfaLoginEndpoint}
                    token={mfaToken}
                    onSuccess={() => setMfaModalOpen(false)}
                />
            )}
            {children}
        </>
    );
}
